import { useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { updateCrop, STAGES, SOWING_TYPES } from "@/lib/api";

/**
 * Inline edit dialog for a single crop. Used from the bed designer popover
 * so the user doesn't have to leave the Designer tab to tweak details.
 */
export function CropEditDialog({ crop, open, onOpenChange, onSaved }) {
    const [form, setForm] = useState(() => ({
        variety: crop?.variety || "",
        plant_count: crop?.plant_count ?? 1,
        sowing_type: crop?.sowing_type || "direct",
        sowing_date_planned: crop?.sowing_date_planned || "",
        transplant_date_planned: crop?.transplant_date_planned || "",
        harvest_window_start: crop?.harvest_window_start || "",
        stage: crop?.stage || "planned",
        notes: crop?.notes || "",
    }));
    const [saving, setSaving] = useState(false);

    if (!crop) return null;

    const submit = async () => {
        setSaving(true);
        const payload = { ...form };
        ["sowing_date_planned", "transplant_date_planned", "harvest_window_start"].forEach(k => {
            if (!payload[k]) payload[k] = null;
        });
        try {
            await updateCrop(crop.id, payload);
            toast.success(`${crop.crop_name} updated`);
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (detail?.message || e.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[var(--vm-card)] border-[var(--vm-border)] max-w-lg" data-testid="crop-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif-display text-2xl">
                        Edit {crop.crop_name}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-2 max-h-[60vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-2">
                            <Label>Variety</Label>
                            <Input
                                data-testid="edit-crop-variety"
                                value={form.variety}
                                onChange={e => setForm({ ...form, variety: e.target.value })}
                                placeholder="e.g. Cherokee Purple"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label>Plant count</Label>
                            <Input
                                type="number"
                                min="1"
                                data-testid="edit-crop-count"
                                value={form.plant_count}
                                onChange={e => setForm({ ...form, plant_count: Number(e.target.value) })}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-2">
                            <Label>Sowing</Label>
                            <Select value={form.sowing_type} onValueChange={v => setForm({ ...form, sowing_type: v })}>
                                <SelectTrigger data-testid="edit-crop-sowing"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {SOWING_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid gap-2">
                            <Label>Stage</Label>
                            <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                                <SelectTrigger data-testid="edit-crop-stage"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="grid gap-2">
                            <Label>Sow date</Label>
                            <Input type="date" data-testid="edit-crop-sow-date"
                                   value={form.sowing_date_planned}
                                   onChange={e => setForm({ ...form, sowing_date_planned: e.target.value })} />
                        </div>
                        <div className="grid gap-2">
                            <Label>Transplant</Label>
                            <Input type="date" data-testid="edit-crop-transplant-date"
                                   value={form.transplant_date_planned}
                                   onChange={e => setForm({ ...form, transplant_date_planned: e.target.value })} />
                        </div>
                        <div className="grid gap-2">
                            <Label>Harvest from</Label>
                            <Input type="date" data-testid="edit-crop-harvest-date"
                                   value={form.harvest_window_start}
                                   onChange={e => setForm({ ...form, harvest_window_start: e.target.value })} />
                        </div>
                    </div>
                    <div className="grid gap-2">
                        <Label>Notes</Label>
                        <Textarea data-testid="edit-crop-notes" value={form.notes}
                                  onChange={e => setForm({ ...form, notes: e.target.value })} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="edit-crop-save"
                            className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                        {saving
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                            : <><Save className="w-4 h-4 mr-2" /> Save</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
